<script setup>
import { fix, onClickOutside } from '#imports'

import { useStore } from '~/store'

import xImage from '@/components/common/xImage.vue'
import Sort from '@/components/futures/icons/Sort.vue'
import NoData from '@/components/common/NoData.vue'
import IconChevronDown from '@/assets/images/common/icon-chevron-down.svg'
import IconFav from '@/assets/images/common/icon-fav-trade.svg'
import { useFutureStore } from '~/store/future'

const { t } = useI18n()
const store = useStore()
const future = useFutureStore()
const router = useRouter()

const type = ref('fav')
const regionType = ref(0)
const show = ref(false)
const listRef = ref(null)
const sorter = ref([])
const searchText = ref('')
const primaryTypes = computed(() => [
  { label: t('自选'), key: 'fav' },
  { label: t('全部'), key: 'futures' },
])
const dt = computed(() => [
  { label: t('名称'), key: 'code', class: 'w-[150px]' },
  { label: t('最新价'), key: 'lp', class: 'w-[100px]' },
  { label: t('24涨跌幅'), key: 'r', class: 'justify-end flex-1 text-end shrink-0' },
])
const regions = computed(() => [{ key: 0, name: t('永续合约'), list: store.visibleContracts }])
const list = computed(() => type.value === 'fav' ? store.visibleContracts.filter(i => future.favIds.includes(i.id)) : regions.value.find(i => i.key === regionType.value).list ?? [])

const searchList = computed(() => {
  const reg = new RegExp(searchText.value, 'gi')
  return list.value.filter(i => reg.test(i.base) || reg.test(i.displayName))
})
const dd = computed(() => {
  const [key, direct] = sorter.value
  const arr = searchText.value ? [...searchList.value] : [...list.value]
  if (!direct)
    return arr

  if (key === 'code') {
    arr.sort((a, b) => b[key] > a[key] ? 1 : -1)
  }
  else {
    const defaultValue = direct < 0 ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY
    arr.sort((a, b) => {
      const ac = future.getOverviewByCode(a.code)?.[key] ?? defaultValue
      const bc = future.getOverviewByCode(b.code)?.[key] ?? defaultValue
      return bc - ac
    })
  }
  if (direct < 0)
    arr.reverse()

  return arr
})

onClickOutside(listRef, () => {
  setTimeout(() => {
    show.value = false
  })
})

function switchSymbol(code) {
  router.replace({ params: { symbol: code } })
  show.value = false
}
</script>

<template>
  <div class="relative pe-4">
    <div class="flex items-center cursor-pointer">
      <div class="flex items-center select-none" @click="show = true">
        <xImage :src="store.getSpotIconByCode(future.base)" :alt="future.base" class="w-6 h-6 me-2 shrink-0" />
        <div class="flex items-center font-medium text-textPrimary">
          <span class="me-1">{{ future.curInfo.displayName }}</span>
          <IconChevronDown class="w-4 h-4 transition" :class="{ 'rotate-180': show }" />
        </div>
      </div>
      <button class="flex items-center justify-center w-6 h-6 ms-1 border rounded border-border" @click="store.toggleFav(future.id, 2)">
        <IconFav class="w-4 h-4 text-textSecondary" :class="{ '!text-orange3': future.favIds.includes(future.id) }" />
      </button>
    </div>
    <div
      v-if="show"
      ref="listRef"
      style="transform: translateZ(0);"
      class="absolute z-10 border border-border top-9 start-0 bg-pageSecondary w-[500px] flex flex-col pt-4 h-[584px] rounded-2xl drop-shadow-[4px_10px_10px_#00000025]"
    >
      <n-input v-model:value="searchText" :placeholder="$t('搜索交易对')" size="large" style="--n-height: 48px" class="mx-4 !w-auto">
        <template #prefix>
          <SvgoCommonIconSearch class="w-4 h-4 me-1" />
        </template>
        <template #suffix>
          <svgo-common-search-clear v-show="searchText" class="w-5 h-5 cursor-pointer" @click="searchText = ''" />
        </template>
      </n-input>
      <div class="flex items-center h-[34px] space-x-6 rtl:space-x-reverse border-b border-border mt-2 mx-4">
        <div v-for="i in primaryTypes" :key="i.key" class="h-full pt-2 font-medium border-b border-transparent cursor-pointer text-textSecondary" :class="{ '!text-textPrimary !border-textPrimary': i.key === type }" @click="type = i.key">
          {{ i.label }}
        </div>
      </div>
      <div v-if="type === 'futures'" class="flex flex-wrap items-center gap-3 px-4 my-2">
        <button
          v-for="i in regions" :key="i.key" class="h-6 px-2 text-xs text-textTertiary" :class="{
            'bg-pageTertiary !text-textPrimary': regionType === +i.key,
          }"
          @click="regionType = +i.key"
        >
          {{ i.name }}
        </button>
      </div>
      <div class="flex items-center h-5 mx-4 mt-3 text-xs text-textSecondary whitespace-nowrap">
        <Sort v-for="i in dt" :key="i.key" v-model="sorter" :type="i.key" :class="i.class">
          {{ i.label }}
        </Sort>
      </div>
      <NoData v-if="!dd.length" type="search" class="mx-4" icon-class="!w-[128px] !h-[96px]" />
      <n-scrollbar v-else class="flex-1 mt-1.5">
        <div v-for="i in dd" :key="i.code" class="flex items-center h-10 px-4 text-xs text-textPrimary font-medium cursor-pointer hover:bg-inputHovered w-[500px]" :class="{ 'bg-inputHovered': i.code === future.code }" @click="switchSymbol(i.code)">
          <div class="flex items-center text-xs font-medium w-[150px]">
            <IconFav class="w-4 h-4 text-textSecondary shrink-0" :class="{ '!text-orange3': future.favIds.includes(i.id) }" @click.stop="store.toggleFav(i.id, 2)" />
            <!-- <xImage loading="lazy" :src="store.getBaseIconByCode(i.code)" class="w-4 h-4 me-1 ms-2.5 shrink-0" /> -->
            <span class="ps-2">{{ i.displayName }}</span>
          </div>
          <div class="w-[100px]">
            {{ fix(future.getOverviewByCode(i.id)?.lp, store.contractsRaw[i.id].ps || 2) ?? '--' }}
          </div>
          <div class="flex-1 text-end" :class="{ 'text-buy': future.getOverviewByCode(i.id)?.r > 0, 'text-sell': future.getOverviewByCode(i.id)?.r < 0 }">
            {{ future.getOverviewByCode(i.id)?.rt ?? '--' }}
          </div>
        </div>
      </n-scrollbar>
    </div>
  </div>
</template>
